import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { TranslationService } from '../../../services/translation.service'
import { MatDivider } from '@angular/material/divider'
import {
  FULL_INGREDIENT_DISCLOSURE,
  PARTIAL_INGREDIENT_DISCLOSURE,
} from '../../../constants/sustainability-highlights'
import { ProductHighlights } from '../../../types/products'
import { SustainabilityHighlight } from '../../../types/sustainabilityHighlight'
import { NgOptimizedImage } from '@angular/common'
import { MatTooltip } from '@angular/material/tooltip'
import { ProvincialRecyclabilityLabelComponent } from './provincial-recyclability-label/provincial-recyclability-label.component'
import { GhsClassification } from '../../../types/ghs'
import { CountryRecyclabilityHighlightComponent } from './country-recyclability-highlight/country-recyclability-highlight.component'

@Component({
  selector: 'app-sustainability-highlights',
  imports: [
    MatDivider,
    NgOptimizedImage,
    MatTooltip,
    ProvincialRecyclabilityLabelComponent,
    CountryRecyclabilityHighlightComponent,
  ],
  templateUrl: './sustainability-highlights.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sustainability-highlights.component.scss',
})
export class SustainabilityHighlightsComponent implements OnChanges {
  translationService = inject(TranslationService)

  @Input() productHighlights?: ProductHighlights
  @Input() isMobileView: boolean = false
  @Input() ghsClassifications: Array<GhsClassification> = []

  sustainabilityHighlights: Array<SustainabilityHighlight> = []

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['productHighlights'] &&
      changes['productHighlights'].currentValue
    ) {
      if (this.productHighlights) {
        this.sustainabilityHighlights = this.getSustainabilityHighlights(
          this.productHighlights,
        )
      }
    }
  }

  /**
   * Returns the translated text for a given GHS hazard code
   * @param {string} code - The GHS hazard code (e.g., 'ghs01', 'ghs02', etc.)
   * @returns {string} The translated hazard text
   */
  getGhsHazardText(code: string): string {
    switch (code.toLowerCase()) {
      case 'ghs01':
        return this.translationService.getGhsExplosiveText()
      case 'ghs02':
        return this.translationService.getGhsFlammableText()
      case 'ghs03':
        return this.translationService.getGhsOxidizingText()
      case 'ghs04':
        return this.translationService.getGhsCompressedGasText()
      case 'ghs05':
        return this.translationService.getGhsCorrosiveText()
      case 'ghs06':
        return this.translationService.getGhsToxicText()
      case 'ghs07':
        return this.translationService.getGhsHarmfulText()
      case 'ghs08':
        return this.translationService.getGhsHealthHazardText()
      case 'ghs09':
        return this.translationService.getGhsEnvironmentalHazardText()
      default:
        return ''
    }
  }

  /**
   * Returns file path for a GHS classification image
   * @param {string} ghs Name of GHS classification
   * @returns {string} The file path
   */
  getGhsImgPath(ghsCode: string): string {
    return `assets/ghs/${ghsCode.toLowerCase()}.svg`
  }

  /**
   * Returns an array of Sustainability Highlights for a Product
   * @returns {Array<SustainabilityHighlight>} Array of product sustainability highlights
   * @param product
   */
  getSustainabilityHighlights(
    product: ProductHighlights,
  ): Array<SustainabilityHighlight> {
    const sustainabilityHighlights: Array<SustainabilityHighlight> = []
    if (product.fullIngredientDisclosure) {
      sustainabilityHighlights.push(FULL_INGREDIENT_DISCLOSURE)
    } else {
      sustainabilityHighlights.push(PARTIAL_INGREDIENT_DISCLOSURE)
    }

    return sustainabilityHighlights
  }

  roundToNearestWhole(value: number): number {
    return Math.round(value)
  }
}
